export type ChatCursorEvent = Record<string, unknown>;

export type ChatTranscriptBlock =
  | { kind: 'text'; text: string }
  | { kind: 'thinking'; text: string; seconds: number }
  | { kind: 'tool'; name: string; status: string; provider: string; detail: string; output: string }
  | { kind: 'task'; title: string; status: string }
  | { kind: 'status'; text: string };

function neutralize(label: string): string {
  return label
    .replace(/\bCursor SDK\b/gi, 'AI agent')
    .replace(/\bCursor environment\b/gi, 'AI environment')
    .replace(/\bCursor agent\b/gi, 'AI agent')
    .replace(/\bCursor chat\b/gi, 'AI chat')
    .replace(/\bCursor stream\b/gi, 'AI response')
    .replace(/\bcursor\b/gi, 'AI');
}

export function chatCursorEventLabel(event: ChatCursorEvent): string {
  const type = String(event.type || '');
  if (type === 'status') {
    const status = String(event.status || '').trim();
    const message = String(event.message || status.replace(/_/g, ' ') || '').trim();
    if (status.toUpperCase() === 'THINKING' && message.toLowerCase() === 'thinking') return '';
    return neutralize(message);
  }
  if (type === 'run_started') return 'Agent connected';
  if (type === 'cursor_task') {
    const text = String(event.text || '').trim();
    return text ? `Delegated task: ${neutralize(text)}` : 'Delegated task activity';
  }
  if (type === 'tool_start') return `Calling ${String(event.name || 'tool')}`;
  if (type === 'tool_done') {
    const status = String(event.status || '');
    return `${String(event.name || 'tool')} ${status === 'completed' ? 'completed' : status || 'finished'}`;
  }
  return type.replace(/_/g, ' ');
}

function isThinking(event: ChatCursorEvent): boolean {
  return String(event.type || '') === 'status' && String(event.status || '').trim().toUpperCase() === 'THINKING';
}

function appendThinking(current: string, fragment: string): string {
  const text = fragment.trim();
  if (!text) return current;
  if (!current) return text;
  if (/^[.,!?;:)]$/.test(text)) return `${current}${text}`;
  return `${current} ${text}`;
}

function flushThinking(blocks: ChatTranscriptBlock[], thinkingText: string, thinkingMs: number): { text: string; ms: number } {
  const text = thinkingText.trim().replace(/\s+/g, ' ');
  const seconds = thinkingMs > 0 ? Math.max(1, Math.round(thinkingMs / 1000)) : 0;
  if ((text && text.toLowerCase() !== 'thinking') || seconds > 0) {
    blocks.push({ kind: 'thinking', text: text.toLowerCase() === 'thinking' ? '' : text, seconds });
  }
  return { text: '', ms: 0 };
}

export function buildChatTranscript(events: ChatCursorEvent[] | undefined, content: string): ChatTranscriptBlock[] {
  const blocks: ChatTranscriptBlock[] = [];
  let thinkingText = '';
  let thinkingMs = 0;
  let sawTextDelta = false;
  const openTools = new Map<string, number>();
  for (const event of events || []) {
    const type = String(event.type || '');
    if (type === 'assistant_delta') {
      ({ text: thinkingText, ms: thinkingMs } = flushThinking(blocks, thinkingText, thinkingMs));
      const text = String(event.text || '');
      if (!text) continue;
      sawTextDelta = true;
      const last = blocks[blocks.length - 1];
      if (last?.kind === 'text') last.text += text;
      else blocks.push({ kind: 'text', text });
      continue;
    }
    if (isThinking(event)) {
      const label = chatCursorEventLabel(event).trim();
      if (label) thinkingText = appendThinking(thinkingText, label);
      const durationMs = Number(event.durationMs || 0);
      if (durationMs > 0) thinkingMs += durationMs;
      continue;
    }
    ({ text: thinkingText, ms: thinkingMs } = flushThinking(blocks, thinkingText, thinkingMs));
    if (type === 'tool_start' || type === 'tool_done') {
      const name = String(event.name || 'tool');
      const callId = String(event.callId || `${name}:${String(event.detail || '')}`);
      const status = type === 'tool_start' ? 'running' : String(event.status || 'finished');
      const provider = String(event.provider || '');
      const detail = String(event.detail || '');
      const output = String(event.output || '');
      const openIndex = openTools.get(callId);
      if (type === 'tool_done' && openIndex !== undefined && blocks[openIndex]?.kind === 'tool') {
        const current = blocks[openIndex];
        blocks[openIndex] = {
          kind: 'tool',
          name,
          status,
          provider: provider || current.provider,
          detail: detail || current.detail,
          output: output || current.output,
        };
        openTools.delete(callId);
      } else {
        blocks.push({ kind: 'tool', name, status, provider, detail, output });
        if (type === 'tool_start') openTools.set(callId, blocks.length - 1);
      }
      continue;
    }
    if (type === 'cursor_task') {
      const title = String(event.text || '').trim() || 'Delegated task';
      blocks.push({ kind: 'task', title: neutralize(title), status: String(event.status || '') });
      continue;
    }
    const label = chatCursorEventLabel(event).trim();
    if (!label) continue;
    const last = blocks[blocks.length - 1];
    if (last?.kind === 'status' && last.text === label) continue;
    blocks.push({ kind: 'status', text: label });
  }
  ({ text: thinkingText } = flushThinking(blocks, thinkingText, thinkingMs));
  if (!sawTextDelta && String(content || '').trim()) blocks.push({ kind: 'text', text: String(content) });
  return blocks;
}
